import type { Locale } from "@commerce-frontend/i18n/types";
import type { BlocksFragmentFragment } from "@commerce-frontend/types";
import { Container } from "@commerce-frontend/ui/base/Container/Container";
import { Section as BaseSection } from "@commerce-frontend/ui/base/Section/Section";
import type { ReactNode } from "react";
import { SectionHeader } from "~/components/SectionHeader/SectionHeader";
import { TeaserRow } from "~/components/TeaserRow/TeaserRow";
import { ArticleOverview } from "~/components/cms/ArticleOverview/ArticleOverview";
import { BlockQuote } from "~/components/cms/BlockQuote/BlockQuote";
import { Buttons } from "~/components/cms/Buttons/Buttons";
import { CategorySection } from "~/components/cms/CategorySection/CategorySection";
import { FourColumns } from "~/components/cms/Columns/FourColumns";
import { ThreeColumns } from "~/components/cms/Columns/ThreeColumns";
import { TwoColumns } from "~/components/cms/Columns/TwoColumns";
import { CookieDeclaration } from "~/components/cms/CookieDeclaration/CookieDeclaration";
import { CountrySpecificContainer } from "~/components/cms/CountrySpecificContainer/CountrySpecificContainer";
import { Divider } from "~/components/cms/Divider/Divider";
import { FAQItemList } from "~/components/cms/FAQItemList/FAQItemList";
import { Iframe } from "~/components/cms/Iframe/Iframe";
import { Image } from "~/components/cms/Image/Image";
import { NewsletterCTA } from "~/components/cms/NewsletterCTA/NewsletterCTA";
import { RichText } from "~/components/cms/RichText/RichText";
import { Section } from "~/components/cms/Section/Section";
import { ShopSection } from "~/components/cms/ShopSection/ShopSection";
import { Table } from "~/components/cms/Table/Table";
import { UGCFlowbox } from "~/components/cms/UGCFlowbox/UGCFlowbox";
import { Video } from "~/components/cms/Video/Video";
import { Jafix } from "../Jafix/Jafix";

type Props = {
	block: BlocksFragmentFragment;
	locale: Locale;
	searchParams?: URLSearchParams;
	wrapContent?: boolean;
};

export const RenderBlock = ({ block, locale, searchParams, wrapContent = true }: Props) => {
	if (!("__typename" in block)) {
		return null;
	}

	const wrapInSection = wrapContent ? "section" : null;
	const wrapInContainer = wrapContent ? "container" : null;
	switch (block.__typename) {
		case "Section":
			return <Section data={block} key={block.id} locale={locale} />;
		case "Iframe":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<Iframe data={block} />
				</RenderBlockWrapper>
			);
		case "Jafix":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<Jafix data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "Image":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					{/* eslint-disable-next-line jsx-a11y/alt-text */}
					<Image data={block} />
				</RenderBlockWrapper>
			);
		case "Divider":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInContainer}>
					<Divider className="my-7xl" data={block} />
				</RenderBlockWrapper>
			);
		case "TeaserRow":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<TeaserRow data={block} />
				</RenderBlockWrapper>
			);
		case "RichText":
			if (!block.content) return;
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<RichText data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "TwoColumns":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<TwoColumns data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "ThreeColumns":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<ThreeColumns data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "FourColumns":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<FourColumns data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "SectionHeader":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<SectionHeader data={block} />
				</RenderBlockWrapper>
			);
		case "Buttons":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<Buttons data={block} />
				</RenderBlockWrapper>
			);
		case "BlockQuote":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<BlockQuote data={block} />
				</RenderBlockWrapper>
			);
		case "CategorySection":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<CategorySection data={block} />
				</RenderBlockWrapper>
			);
		case "ShopSection":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<ShopSection data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "Table":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<Table data={block} />
				</RenderBlockWrapper>
			);
		case "NewsletterCTA":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<NewsletterCTA data={block} />
				</RenderBlockWrapper>
			);
		case "UGCFlowbox":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<UGCFlowbox data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "CookieDeclaration":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<CookieDeclaration data={block} locale={locale} />
				</RenderBlockWrapper>
			);
		case "ArticleOverview":
			if (!searchParams) return;
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<ArticleOverview data={block} locale={locale} searchParams={searchParams} />
				</RenderBlockWrapper>
			);
		case "Video":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<Video data={block} />
				</RenderBlockWrapper>
			);
		case "FAQItemList":
			return (
				<RenderBlockWrapper key={block.id} wrapType={wrapInSection}>
					<FAQItemList data={block} />
				</RenderBlockWrapper>
			);
		case "CountrySpecificContainer":
			if (!block.countrySpecificContainerBody?.length) return;
			return <CountrySpecificContainer key={block.id} data={block} locale={locale} />;
	}
};

type WrapType = "section" | "container" | null;

const RenderBlockWrapper = ({
	children,
	wrapType = "section",
}: {
	children: ReactNode;
	wrapType?: WrapType;
}) => {
	if (!wrapType) return children;

	if (wrapType === "container") {
		return <Container>{children}</Container>;
	}

	return (
		<BaseSection>
			<Container>{children}</Container>
		</BaseSection>
	);
};
